import React, { useEffect, useState } from "react";
import { FeedModal } from "./FeedModal";
import { Close, User } from "assets/svg";
import { useEventStore, useGuestStore } from "store";
import { FormattedMessage, useIntl } from "react-intl";
import ImagePreview from "./ImagePreview";
import * as SpacesService from "libs/digitalocean/SpacesService";
import { useCreatePost } from "./useCreatePost";
import ImageCropperWithFrame from "components/ImageCropperWithFrame";

interface CreatePostModalProps {
  gameResultText?: string;
  createPost: boolean;
  onClose: () => void;
  setPost: (isOpen: boolean) => void;
  onPostCreated?: (post: any) => void;
  frameSelector?: boolean;
  frameStyle?: "bingo" | "photo-challenge" | "team-match" | "quiz";
  gameTag?: string;
}

export default function CreatePost({
  createPost,
  gameResultText,
  setPost,
  onClose,
  onPostCreated,
  frameSelector,
  frameStyle,
  gameTag,
}: CreatePostModalProps) {
  const { guest } = useGuestStore();
  const { theme } = useEventStore();
  const intl = useIntl();
  const [guestPhotoUrl, setGuestPhotoUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const {
    newPostText,
    setNewPostText,
    selectedImage,
    setSelectedImage,
    croppedImage,
    setCroppedImage,
    tempImageFile,
    setTempImageFile,
    showImageResizer,
    setShowImageResizer,
    isCreating,
    creationProgress,
    submitNewPost,
    resetForm,
  } = useCreatePost();

  useEffect(() => {
    const fetchGuestPhoto = async () => {
      try {
        const url = await SpacesService.getFileUrl(
          guest?.photoStoragePath as string
        );
        setGuestPhotoUrl(url);
      } catch (error) {
        console.error("Error fetching guest photo:", error);
      }
    };

    if (guest?.photoStoragePath) {
      fetchGuestPhoto();
    }
  }, [guest]);

  const handleCloseModal = () => {
    resetForm();
    setError(null);
    onClose();
  };

  const handleSubmitPost = async (e: React.FormEvent) => {
    e.preventDefault();

    if (isCreating) return;

    setError(null);

    try {
      await submitNewPost(
        guestPhotoUrl,
        guest?.name,
        (post) => {
          onPostCreated?.(post);
          handleCloseModal();
          setPost(false);
        },
        gameTag
      );
    } catch (error) {
      console.error("Error creating post:", error);
      setError(
        error instanceof Error
          ? error.message
          : "Error to create post. Try again."
      );
    }
  };

  const isFormValid = newPostText.trim() || selectedImage;

  const onFinishedCrop = (croppedFile: File) => {
    setCroppedImage(croppedFile);
    setTempImageFile(null);
    setShowImageResizer(false);
  };

  const onCancelCrop = () => {
    setTempImageFile(null);
    setCroppedImage(null);
    setSelectedImage(null);
    setShowImageResizer(false);
  };

  const handleSetPostText = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setNewPostText(e.target.value);
  };

  useEffect(() => {
    if (gameResultText) {
      setNewPostText(gameResultText);
    }
  }, [gameResultText, setNewPostText]);

  return (
    <FeedModal isOpen={createPost} onClose={handleCloseModal}>
      <>
        {!showImageResizer ? (
          <form
            onSubmit={handleSubmitPost}
            className="h-full w-full flex flex-col"
          >
            <header className="h-8 flex items-center justify-center mt-3">
              <h2 className="font-century-gothic-bold text-lg">
                <FormattedMessage
                  id="feed.createPost.title"
                  defaultMessage="Novo Post"
                />
              </h2>

              <button
                type="button"
                onClick={handleCloseModal}
                className="absolute top-4 right-4 p-0 z-10"
                disabled={isCreating}
              >
                <Close width={24} height={24} color="none" outline="#414141" />
              </button>
            </header>

            <div className="flex-1 flex flex-col">
              <div className={`flex items-center gap-2 pl-4 pr-2 py-2`}>
                <div className="w-14 h-14 grow-0 shrink-0 rounded-full overflow-hidden border-2 bg-white border-gray-200">
                  {guestPhotoUrl ? (
                    <img
                      src={guestPhotoUrl}
                      alt={guest?.name || "Guest"}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <User
                        width={24}
                        height={24}
                        color="none"
                        outline="#414141"
                      />
                    </div>
                  )}
                </div>
                <div className="relative flex-1 h-[74px] flex items-center">
                  {Boolean(gameResultText) ? (
                    <p className="text-gray-600 text-sm text-left px-2">
                      {gameResultText}
                    </p>
                  ) : (
                    <textarea
                      rows={2}
                      disabled={isCreating || Boolean(gameResultText)}
                      placeholder={intl.formatMessage({
                        id: "feed.placeholder.newPostMessage",
                        defaultMessage: "Escreva sua mensagem...",
                      })}
                      value={newPostText}
                      onChange={handleSetPostText}
                      className={`${
                        theme.card
                      } card w-full shadow-none bg-white font-century-gothic text-theme-black border border-gray-200 rounded-xl px-4 py-3 resize-none ${
                        theme.name === "rose"
                          ? "focus:border-primary-rose"
                          : "focus:border-gray-600"
                      } focus:border-2 focus:outline-none`}
                      maxLength={1000}
                    />
                  )}

                  <div className="absolute right-2 bottom-1 text-[0.5rem] text-gray-200">
                    <span>{newPostText.length}/1000</span>
                  </div>
                </div>
              </div>

              <div className="px-2">
                <ImagePreview
                  selectedImage={croppedImage || selectedImage}
                  originalImage={selectedImage}
                  setSelectedImage={setSelectedImage}
                  setCroppedImage={setCroppedImage}
                  setTempImageFile={setTempImageFile}
                  setShowImageResizer={setShowImageResizer}
                />
              </div>

              {isCreating && (
                <div className="px-4 py-2">
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${creationProgress}%` }}
                    ></div>
                  </div>
                  <p className="text-sm text-gray-600 mt-1 text-center">
                    {creationProgress < 70
                      ? intl.formatMessage({
                          id: "feed.createPost.info.sendingPostImage",
                          defaultMessage: "Enviando imagem...",
                        })
                      : creationProgress < 100
                      ? intl.formatMessage({
                          id: "feed.createPost.info.creatingPost",
                          defaultMessage: "Criando post...",
                        })
                      : intl.formatMessage({
                          id: "feed.createPost.info.finalizingPost",
                          defaultMessage: "Finalizando...",
                        })}
                  </p>
                </div>
              )}

              {error && (
                <div className="px-4 py-2">
                  <p className="text-red-600 text-sm text-center bg-red-50 p-2 rounded">
                    {error}
                  </p>
                </div>
              )}
            </div>

            <div className="p-3 flex items-center justify-center">
              <button
                type="submit"
                className={`${theme.button} px-8 disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none`}
                disabled={!isFormValid || isCreating}
              >
                {isCreating ? (
                  <FormattedMessage
                    id="feed.button.creatingPost"
                    defaultMessage="Criando..."
                  />
                ) : (
                  <FormattedMessage
                    id="feed.button.createPost"
                    defaultMessage="Postar"
                  />
                )}
              </button>
            </div>
          </form>
        ) : (
          <div className="w-full h-full flex flex-col items-center justify-center p-4 min-w-[360px]">
            <button
              type="button"
              onClick={() => {
                setTempImageFile(null);
                setShowImageResizer(false);
              }}
              className="absolute top-3 right-4 p-0"
            >
              <Close width={24} height={24} color="none" outline="#414141" />
            </button>

            <h3 className="text-lg font-semibold mb-4 text-center">
              <FormattedMessage
                id="feed.editImage.title"
                defaultMessage="Editar Imagem"
              />
            </h3>

            <ImageCropperWithFrame
              file={tempImageFile}
              onFinish={onFinishedCrop}
              onCancel={onCancelCrop}
              frameStyle={frameStyle}
              frameSelector={frameSelector}
            />
          </div>
        )}
      </>
    </FeedModal>
  );
}
